<style lang="scss">
  @use "../../../../assets/css/global.scss" as *;
  @use "../../../../assets/css/_colors.scss" as *;
  @use "../../../../assets/css/_mixins.scss" as *;
  .scrollArea {
    max-height: 100%;
    overflow-y: auto;
    overflow-x: clip;

    .comparisonGraph {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      position: relative;

      .graphContainer {
        display: flex;

        .namesColumn {
          display: flex;
          flex-direction: column;
        }

        .graphColumn {
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          position: relative;

          .incrementLineContainer {
            left: 0;
            position: absolute;
            top: 0;
            @include size(100%);

            .incrementLine {
              width: 1px;
              background-color: $light-gray;
              bottom: 0;
              position: absolute;
              top: 0;

              &Average {
                width: 2px;
                background-color: transparent;
                z-index: 9;
                @include dotted-background($primary-color, 4px, 4px);
              }
            }
          }

          .row {
            width: 100%;
            align-items: center;
            display: flex;
            $border-radius: 0.625rem;
            margin-left: 0rem;

            .graphBar {
              width: 100%;
              background-color: $light-blue;
              border-radius: $border-radius;
              overflow: hidden;
              position: relative;

              .nullData {
                height: 100%;
                background-color: transparent;
                position: absolute;
                display: flex;
                justify-content: flex-end;
                padding-right: 0.2rem;

                .nullStatValue {
                  position: absolute;
                  top: 50%;
                  transform: translateY(-50%);
                  z-index: 10;

                  color: $primary-color;
                  left: calc(100% + 0.2rem);
                  text-wrap: nowrap;
                }
              }

              .progressBar {
                height: 100%;
                background-color: $primary-color;
                border-radius: $border-radius;
                position: absolute;
                display: flex;
                justify-content: flex-end;
                padding-right: 0.2rem;
                .statValue {
                  color: white;
                  position: absolute;
                  top: 50%;
                  transform: translateY(-50%);
                  z-index: 10;

                  &.inside {
                    right: 0.4rem;
                  }

                  &.outside {
                    color: $primary-color;
                    left: calc(100% + 0.2rem);
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  .labelsContainer {
    display: flex;
    margin-top: 0.6rem;
    margin-bottom: 2rem;
    position: relative;

    .incrementLabelContainer {
      position: relative;

      .incrementLabel {
        font-size: 0.75rem;
        position: absolute;

        span {
          @include absolute-center-horizontal;
          background-color: white;
          z-index: 10;
        }
      }

      .overallAverage {
        color: $primary-color;
        font-size: 0.75rem;
        font-weight: bold;
        position: absolute;
        text-align: center;
        top: 1.5rem;

        span {
          white-space: nowrap;
          @include absolute-center-horizontal;
        }

        &::before {
          $height: 1rem;
          width: 2px;
          height: $height;
          bottom: $height;
          content: "";
          position: absolute;
          z-index: 9;
          @include dotted-background($primary-color, 4px, 4px);
        }
      }
    }
  }
</style>

<script>
  import NameWrapper from "./NameWrapper.svelte";
  import {
    calculateRankingMapping,
    formatDatum,
    sortData,
  } from "./utils/utils";

  export let ticks = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
  export let data = [];
  export let dataKey = "";
  export let dataType = "%";
  export let overallAverage = null;
  export let prioritizeCurrentUser = false;
  export let showRanking = false;
  export let graphBarHeight = 0.9;
  export let rowGap = "1rem";
  export let nullDataMessage = "no data available";

  export let height = null;
  export let bottomPadding = null;

  $: showNullDataMessage = nullDataMessage && dataType === "%";

  let rowHeight = "2.5rem";
  let namesColumnWidth = 0;
  let namesColumnMarginRight = "2rem";
  let scrollAreaPaddingRight = "1.5rem";

  let graphColumnWidth;
  let incrementLabelContainerStyling = "flex-grow: 1;";
  let fakeNamesColumnStyle = "";

  let hoveredIndex = null;

  let wrapperStyling = "";
  if (height) wrapperStyling += `; height: ${height}`;
  if (bottomPadding) wrapperStyling += `; padding-bottom: ${bottomPadding}`;

  const statValuePositioningClass = (val, maxVal) =>
    val / maxVal > 0.2 ? "inside" : "outside";

  let rankedData = [];
  let rankingMapping = {};

  const showProgressBar = (item) =>
    dataType === "%" ? item[dataKey] !== null : item[dataKey];

  $: {
    if (showRanking) {
      rankingMapping = calculateRankingMapping(data, dataKey);

      rankedData = sortData(data, dataKey, -1).map((item) => ({
        ...item,
        ranking: rankingMapping[item[dataKey]],
        value: item[dataKey],
        show: showProgressBar(item),
      }));
    } else if (prioritizeCurrentUser) {
      rankedData = data
        .sort((a, b) => {
          if (a.isCurrentUser && !b.isCurrentUser) return -1;
          // eslint-disable-next-line sourcewhale/prefer-ternary
          if (!a.isCurrentUser && b.isCurrentUser) return 1;
          return 0;
        })
        .map((item, index) => ({
          ...item,
          ranking: index + 1,
          value: item[dataKey],
          show: showProgressBar(item),
        }));
    } else {
      rankedData = data.map((item, index) => ({
        ...item,
        ranking: index + 1,
        value: item[dataKey],
        show: showProgressBar(item),
      }));
    }
  }

  $: overallAverageValue = overallAverage ? overallAverage[dataKey] : null;
  $: maxTick = Math.max(...ticks);
  $: tickPercentages = ticks.map((tick) => (tick / maxTick) * 100);

  $: requestAnimationFrame(() => {
    incrementLabelContainerStyling = graphColumnWidth
      ? `width: ${graphColumnWidth}px`
      : "flex-grow: 1";
  });

  $: {
    requestAnimationFrame(() => {
      fakeNamesColumnStyle = `width: ${namesColumnWidth}px; margin-right: ${namesColumnMarginRight}`;
    });
  }
</script>

<div class="comparisonGraphWrapper" style={wrapperStyling}>
  <div class="scrollArea" style="padding-right: {scrollAreaPaddingRight}">
    <div class="comparisonGraph">
      <div class="graphContainer">
        <div
          class="namesColumn"
          bind:offsetWidth={namesColumnWidth}
          style="margin-right: {namesColumnMarginRight}; gap: {rowGap}"
        >
          {#each rankedData as item}
            <NameWrapper
              {showRanking}
              ranking={item.ranking}
              {prioritizeCurrentUser}
              user={item}
            />
          {/each}
        </div>
        <div
          class="graphColumn"
          bind:offsetWidth={graphColumnWidth}
          style="gap: {rowGap}"
        >
          <div class="incrementLineContainer">
            {#each tickPercentages as percentage}
              <div class="incrementLine" style="left: {percentage}%;"></div>
            {/each}
            {#if overallAverageValue !== null}
              <div
                class="incrementLine incrementLineAverage"
                style="left: {(overallAverageValue / maxTick) * 100}%"
              ></div>
            {/if}
          </div>
          {#each rankedData as item, index}
            <div
              class="row"
              style="height: {rowHeight}"
              on:mouseenter={() => {
                hoveredIndex = index;
              }}
              on:mouseleave={() => {
                hoveredIndex = null;
              }}
              role="button"
              tabindex="0"
              aria-label={`data row ${index + 1}`}
            >
              <div class="graphBar" style="height: {graphBarHeight}rem">
                {#if item.show}
                  <div
                    class="progressBar"
                    style="width: {(item.value / maxTick) * 100}%;"
                  >
                    {#if hoveredIndex === index}
                      <span
                        class="statValue {statValuePositioningClass(
                          item.value,
                          maxTick
                        )}"
                        style="font-size: {0.7 * graphBarHeight}rem;"
                        >{formatDatum(item.value, dataType)}</span
                      >
                    {/if}
                  </div>
                {:else if showNullDataMessage}
                  <div class="nullData">
                    {#if hoveredIndex === index}
                      <span
                        class="nullStatValue"
                        style="font-size: {0.7 * graphBarHeight}rem;"
                        >{nullDataMessage}</span
                      >
                    {/if}
                  </div>
                {:else}
                  <div class="nullData">
                    {#if hoveredIndex === index}
                      <span
                        class="nullStatValue"
                        style="font-size: {0.7 * graphBarHeight}rem;">0</span
                      >
                    {/if}
                  </div>
                {/if}
              </div>
            </div>
          {/each}
        </div>
      </div>
    </div>
  </div>
  <div class="labelsContainer">
    <div class="fakeNamesColumn" style={fakeNamesColumnStyle}></div>
    <div class="incrementLabelContainer" style={incrementLabelContainerStyling}>
      {#each ticks as tick, i}
        <div class="incrementLabel" style="left: {tickPercentages[i]}%">
          <span>{tick}</span>
        </div>
      {/each}
      {#if overallAverageValue !== null}
        <div
          class="overallAverage"
          style="left: {(overallAverageValue / maxTick) * 100}%"
        >
          <span>SourceWhale Average: {overallAverageValue}%</span>
        </div>
      {/if}
    </div>
  </div>
</div>
